import { Button, ColorPicker, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Table } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";

const ColorPickerField = ({ value, onChange }) => (
  <ColorPicker
    format="hex"
    showText
    value={value ?? null}
    onChange={(_, hex) => onChange(hex)}
  />
);

const SettingsTags = () => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);

  const loadPrograms = () => {
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${progCompany}` : "dict/prog";
    serverFetch(url)
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  };

  const loadTags = () => {
    setLoading(true);
    serverFetch("Plugin/tags")
      .then((data) => {
        setTags(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка загрузки меток");
      });
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    loadTags();
  }, []);

  const openAdd = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({ progId: programs[0]?.id, idx: 0 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingTag(record);
    form.setFieldsValue({
      name: record.name,
      progId: record.progId,
      color: record.color ?? "",
      idx: record.idx ?? 0,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      setLoading(true);
      const payload = {
        name: values.name,
        progId: values.progId,
        color: values.color || null,
        idx: values.idx ?? 0,
      };
      if (editingTag) {
        payload.id = editingTag.id;
        serverFetch("Plugin/tags", { method: "PUT", bodyData: payload })
          .then(() => {
            setLoading(false);
            message.success("Метка обновлена");
            setModalOpen(false);
            loadTags();
          })
          .catch((e) => {
            setLoading(false);
            message.error(e?.userMessage || "Ошибка сохранения");
          });
      } else {
        serverFetch("Plugin/tags", { method: "POST", bodyData: payload })
          .then(() => {
            setLoading(false);
            message.success("Метка добавлена");
            setModalOpen(false);
            loadTags();
          })
          .catch((e) => {
            setLoading(false);
            message.error(e?.userMessage || "Ошибка сохранения");
          });
      }
    });
  };

  const handleDelete = (id) => {
    setLoading(true);
    serverFetch(`Plugin/tags/${id}`, { method: "DELETE" })
      .then(() => {
        setLoading(false);
        message.success("Метка удалена");
        loadTags();
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const programName = (progId) => programs.find((p) => p.id === progId)?.name ?? progId;

  const columns = [
    { dataIndex: "id", title: "#", width: 70 },
    { dataIndex: "name", title: "Название" },
    {
      dataIndex: "progId",
      title: "Программа",
      render: (progId) => programName(progId),
    },
    {
      dataIndex: "color",
      title: "Цвет",
      width: 120,
      render: (color) =>
        color ? (
          <span
            style={{
              display: "inline-block",
              width: 20,
              height: 20,
              backgroundColor: color,
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: 4,
              verticalAlign: "middle",
            }}
            title={color}
          />
        ) : (
          "—"
        ),
    },
    { dataIndex: "idx", title: "Порядок", width: 90 },
    {
      title: "",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="Удалить метку?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: "90%" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={openAdd}>
          Добавить
        </Button>
        <Button onClick={loadTags} loading={loading}>
          Обновить
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={tags}
        loading={loading}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <Modal
        title={editingTag ? "Редактировать метку" : "Добавить метку"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="progId" label="Программа" rules={[{ required: true }]}>
            <Select
              options={programs.map((p) => ({ label: p.name, value: p.id }))}
              placeholder="Выберите программу"
            />
          </Form.Item>
          <Form.Item name="color" label="Цвет">
            <ColorPickerField />
          </Form.Item>
          <Form.Item name="idx" label="Порядок">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SettingsTags;
